import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { KPI, Panel, EmptyRow } from "@/components/DashboardWidgets";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  ClipboardCheck, Calendar, BookOpen, Award, Wallet, ClipboardList, Library, Bus, FileText, Bell,
} from "lucide-react";

export default function StudentDashboard() {
  const [d, setD] = useState({});
  useEffect(() => { api.get("/stats/student").then((r) => setD(r.data)).catch(() => {}); }, []);
  const s = d.student;
  const att = d.attendance || { total: 0, present: 0, percent: 0 };
  const totalPaid = (d.deposits || []).reduce((a, x) => a + (x.amount || 0), 0);

  if (!s) return <DashLayout variant="student" title="My Dashboard"><div className="text-slate-500">Loading…</div></DashLayout>;

  return (
    <DashLayout variant="student" title="My Dashboard">
      <Card className="p-5 mb-6 flex items-center gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="w-16 h-20 bg-white border-2 border-slate-300 rounded overflow-hidden">
          {s.photo_url ? <img src={s.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="text-[10px] text-slate-400 flex items-center justify-center h-full">Photo</div>}
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{s.first_name} {s.last_name}</h2>
          <div className="text-sm text-slate-600">Class {s.class_name} {s.section || ""} · Reg: {s.registration_no || "—"}</div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div id="st-attendance" className="scroll-mt-6"><KPI icon={ClipboardCheck} label="Attendance" value={`${att.percent}%`} sub={`${att.present}/${att.total}`} color="text-emerald-700 bg-emerald-50" testId="st-att" /></div>
        <div id="st-timetable" className="scroll-mt-6"><KPI icon={Calendar} label="Timetable" value="View" sub="daily" color="text-blue-700 bg-blue-50" testId="st-tt" /></div>
        <KPI icon={BookOpen} label="Exams" value={(d.upcoming_exams || []).length} color="text-indigo-700 bg-indigo-50" testId="st-exams" />
        <KPI icon={Award} label="Results" value={(d.results || []).length} color="text-purple-700 bg-purple-50" testId="st-results" />
        <KPI icon={Wallet} label="Fees Paid" value={`₹${totalPaid.toLocaleString()}`} color="text-emerald-700 bg-emerald-50" testId="st-fees" />
        <div id="st-homework" className="scroll-mt-6"><KPI icon={ClipboardList} label="Homework" value="—" sub="soon" color="text-amber-700 bg-amber-50" testId="st-hw" /></div>
        <div id="st-library" className="scroll-mt-6"><KPI icon={Library} label="Library" value="—" sub="soon" color="text-slate-700 bg-slate-100" testId="st-lib" /></div>
        <div id="st-bus" className="scroll-mt-6"><KPI icon={Bus} label="Bus" value="—" sub="soon" color="text-yellow-700 bg-yellow-50" testId="st-bus" /></div>
        <div id="st-documents" className="scroll-mt-6"><KPI icon={FileText} label="Documents" value="—" sub="soon" color="text-rose-700 bg-rose-50" testId="st-docs" /></div>
        <KPI icon={Bell} label="Notices" value={(d.notices || []).length} color="text-pink-700 bg-pink-50" testId="st-notices" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div id="st-exams" className="scroll-mt-6">
          <Panel title="Upcoming Exams" testId="st-upcoming-exams">
            {(d.upcoming_exams || []).length === 0 ? <EmptyRow text="No upcoming exams" /> : (
              <ul className="space-y-2">
                {d.upcoming_exams.map((e) => (
                  <li key={e.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                    <div><div className="font-semibold">{e.name}</div><div className="text-xs text-slate-500">{e.subject} · {e.max_marks} marks</div></div>
                    <div className="text-xs text-slate-500 font-mono">{e.exam_date}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
        <div id="st-results" className="scroll-mt-6">
          <Panel title="Recent Results" testId="st-recent-results">
            {(d.results || []).length === 0 ? <EmptyRow text="No results yet" /> : (
              <ul className="space-y-2">
                {d.results.map((r) => {
                  const total = Object.values(r.subject_marks || {}).reduce((a, b) => a + (Number(b) || 0), 0);
                  return (
                    <li key={r.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">{Object.entries(r.subject_marks || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</div>
                      <div className="font-bold tabular-nums">{total}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        </div>
        <div id="st-fees" className="scroll-mt-6">
          <Panel title="Recent Payments" testId="st-payments">
            {(d.deposits || []).length === 0 ? <EmptyRow text="No payments" /> : (
              <ul className="space-y-2">
                {d.deposits.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                    <div><div className="font-semibold">{p.fee_type}</div><div className="text-xs text-slate-500">{p.date} · {p.mode}</div></div>
                    <div className="font-bold tabular-nums">₹{(p.amount || 0).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
        <div id="st-notices" className="scroll-mt-6">
          <Panel title="Notices" testId="st-notices-list">
            {(d.notices || []).length === 0 ? <EmptyRow text="No notices" /> : (
              <ul className="space-y-2">
                {d.notices.map((n) => (
                  <li key={n.id} className="text-sm py-2 border-b border-slate-100 last:border-0">
                    <div className="font-semibold">{n.title}</div>
                    <div className="text-xs text-slate-500">{n.event_date}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </DashLayout>
  );
}
